import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

import { PrestamosModel } from '../../../../../modules/finanzas/prestamos/prestamos.model';
import { NotificationService } from '../../../../shared/services/notification.service';
import {
  entityFormDialogConfig,
  EntityFormOption,
  ensureOption,
  fromApiDate,
  isBlank,
  isInvalidMonths,
  isMissingNumber,
  numberOrNull,
  optionId,
  serverErrorMessage,
  SETTLEMENT_OPTIONS,
  TERM_LONG,
  TERM_SHORT,
  toApiDate,
} from '../../../entity-form/entity-form.utils';
import { LoanEntityService } from '../../services/loan-entity.service';
import { LoanFormLookupsFacade } from '../../services/loan-form-lookups.facade';

export interface LoanFormDialogData {
  uid?: string;
}

const TERM_OPTIONS: EntityFormOption[] = [
  { id: TERM_SHORT, display: 'Corto plazo — 520' },
  { id: TERM_LONG, display: 'Largo plazo — 170' },
];

type LoanFormErrors = Partial<Record<
  'duracion' | 'numero' | 'entidad' | 'fechaConstitucion' | 'plazo' | 'fechaFinalizacion' | 'carencia'
  | 'importeInicial' | 'importePendiente' | 'comisionApertura' | 'tipoInteres' | 'tipoLiquidaciones' | 'bankAccount', string>>;

@Component({
  selector: 'app-loan-form-dialog',
  templateUrl: './loan-form-dialog.component.html',
  styleUrls: ['../../../entity-form/entity-form-dialog.scss'],
  standalone: false,
})
export class LoanFormDialogComponent implements OnInit, OnDestroy {
  readonly settlementOptions = [...SETTLEMENT_OPTIONS];
  readonly termOptions = [...TERM_OPTIONS];

  form: UntypedFormGroup;

  ready = false;
  loadError = false;
  saving = false;
  submitted = false;
  saveError = '';
  errors: LoanFormErrors = {};

  bankAccounts: EntityFormOption[] = [];

  private loaded: any = null;
  private readonly destroy$ = new Subject<void>();

  static open(dialog: MatDialog, data: LoanFormDialogData): Observable<boolean> {
    return dialog.open<LoanFormDialogComponent, LoanFormDialogData, boolean>(LoanFormDialogComponent, entityFormDialogConfig(data))
      .afterClosed()
      .pipe(map(result => result === true));
  }

  constructor(
    private fb: UntypedFormBuilder,
    private dialogRef: MatDialogRef<LoanFormDialogComponent, boolean>,
    @Inject(MAT_DIALOG_DATA) public data: LoanFormDialogData,
    private loanEntity: LoanEntityService,
    private lookups: LoanFormLookupsFacade,
    private notify: NotificationService,
  ) {
    this.form = this.fb.group({
      duracion: [null],
      numero: [''],
      entidad: [''],
      fechaConstitucion: [null],
      plazo: [null],
      fechaFinalizacion: [null],
      carencia: [null],
      importeInicial: [null],
      importePendiente: [null],
      comisionApertura: [null],
      tipoInteres: [null],
      tipoLiquidaciones: [null],
      bankAccount: [null],
    });
  }

  get isEdit(): boolean {
    return !!this.data && !!this.data.uid;
  }

  get title(): string {
    return this.isEdit ? 'Editar préstamo' : 'Nuevo préstamo bancario';
  }

  get subtitle(): string {
    if (this.isEdit && this.loaded) {
      return [this.loaded.entidad, this.loaded.numero].filter(Boolean).join(' · ') || 'Modifica los datos del préstamo';
    }
    return 'Registra un nuevo préstamo con una entidad financiera';
  }

  control(name: string): UntypedFormControl {
    return this.form.get(name) as UntypedFormControl;
  }

  get bankAccountControl(): UntypedFormControl {
    return this.control('bankAccount');
  }

  ngOnInit(): void {
    this.form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (this.submitted) this.errors = this.validate();
    });
    this.load();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  retry(): void {
    this.loadError = false;
    this.load();
  }

  save(): void {
    if (this.saving) return;
    this.submitted = true;
    this.saveError = '';
    this.errors = this.validate();
    if (Object.keys(this.errors).length) return;

    const payload = this.buildPayload();
    const request = this.isEdit ? this.loanEntity.update(this.data.uid, payload) : this.loanEntity.create(payload);

    this.saving = true;
    this.dialogRef.disableClose = true;
    request
      .then(() => {
        this.notify.ok(this.isEdit ? 'Préstamo actualizado correctamente' : 'Préstamo creado correctamente');
        this.dialogRef.disableClose = false;
        this.dialogRef.close(true);
      })
      .catch(error => {
        this.saving = false;
        this.dialogRef.disableClose = false;
        this.saveError = serverErrorMessage(error, this.isEdit
          ? 'Ha ocurrido un error inesperado al actualizar el préstamo'
          : 'Ha ocurrido un error inesperado al crear el préstamo');
      });
  }

  close(): void {
    if (this.saving) return;
    this.dialogRef.close(false);
  }

  private load(): void {
    this.ready = false;
    const record: Promise<any> = this.isEdit ? this.loanEntity.get(this.data.uid) : Promise.resolve(null);

    Promise.all([record, this.lookups.bankAccounts()])
      .then(([loan, bankAccounts]) => {
        this.bankAccounts = bankAccounts;
        this.loaded = loan;
        if (loan) this.applyRecord(loan);
        this.ready = true;
      })
      .catch(() => {
        this.loadError = true;
        this.ready = true;
      });
  }

  private applyRecord(loan: any): void {
    this.form.patchValue({
      duracion: ensureOption(this.termOptions, numberOrNull(loan.duracion)),
      numero: loan.numero || '',
      entidad: loan.entidad || '',
      fechaConstitucion: fromApiDate(loan.fechaConstitucion),
      plazo: numberOrNull(loan.plazo),
      fechaFinalizacion: fromApiDate(loan.fechaFinalizacion),
      carencia: numberOrNull(loan.carencia),
      importeInicial: numberOrNull(loan.importeInicial),
      importePendiente: numberOrNull(loan.importePendiente),
      comisionApertura: numberOrNull(loan.comisionApertura),
      tipoInteres: numberOrNull(loan.tipoInteres),
      tipoLiquidaciones: ensureOption(this.settlementOptions, numberOrNull(loan.tipoLiquidaciones)),
      bankAccount: ensureOption(this.bankAccounts, loan.bankAccountsUid, loan.bankAccountInfo),
    }, { emitEvent: false });
  }



  private validate(): LoanFormErrors {
    const value = this.form.getRawValue();
    const errors: LoanFormErrors = {};

    if (optionId(value.duracion) === null) errors.duracion = 'Selecciona la duración';
    if (isBlank(value.numero)) errors.numero = 'Indica el número de préstamo';
    if (isBlank(value.entidad)) errors.entidad = 'Indica la entidad prestamista';
    if (!value.fechaConstitucion) errors.fechaConstitucion = 'Indica la fecha de constitución';
    if (isInvalidMonths(value.plazo)) errors.plazo = 'Indica el plazo en meses';
    if (!value.fechaFinalizacion) errors.fechaFinalizacion = 'Indica la fecha de finalización';
    if (isInvalidMonths(value.carencia)) errors.carencia = 'Indica la carencia en meses';
    if (isMissingNumber(value.importeInicial)) errors.importeInicial = 'Indica el importe inicial';
    if (isMissingNumber(value.importePendiente)) errors.importePendiente = 'Indica el importe pendiente';
    if (isMissingNumber(value.comisionApertura)) errors.comisionApertura = 'Indica la comisión de apertura';
    if (isMissingNumber(value.tipoInteres)) errors.tipoInteres = 'Indica el tipo de interés';
    if (optionId(value.tipoLiquidaciones) === null) errors.tipoLiquidaciones = 'Selecciona el tipo de liquidaciones';
    if (optionId(value.bankAccount) === null) errors.bankAccount = 'Selecciona la cuenta de adeudo';

    return errors;
  }

  private buildPayload(): PrestamosModel {
    const value = this.form.getRawValue();
    const base: any = this.loaded ? JSON.parse(JSON.stringify(this.loaded)) : {};
    return {
      ...base,
      duracion: Number(optionId(value.duracion)),
      numero: String(value.numero).trim(),
      entidad: String(value.entidad).trim(),
      fechaConstitucion: toApiDate(value.fechaConstitucion),
      plazo: Number(value.plazo),
      fechaFinalizacion: toApiDate(value.fechaFinalizacion),
      carencia: Number(value.carencia),
      importeInicial: Number(value.importeInicial),
      importePendiente: Number(value.importePendiente),
      comisionApertura: Number(value.comisionApertura),
      tipoInteres: Number(value.tipoInteres),
      tipoLiquidaciones: Number(optionId(value.tipoLiquidaciones)),
      bankAccountsUid: optionId(value.bankAccount),
    };
  }
}
